import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@adflow/frontend/util-query';
import type {
	AddKeywordDto,
	EditKeywordDto,
	ListKeywordsQuery,
} from '@adflow/shared/data-google-ads';
import {
	AddKeyword,
	DeleteKeyword,
	EditKeyword,
	ListKeywords,
} from '@adflow/shared/data-google-ads';

const keywordKeys = {
	all: ['google-ads', 'template'] as const,
	allList: () => [...keywordKeys.all, 'list'] as const,
	list: (query: ListKeywordsQuery) =>
		[...keywordKeys.allList(), query] as const,
	view: (templateId: number) =>
		[...keywordKeys.all, 'view', templateId] as const,
};

export function useListKeywordsQuery(query: ListKeywordsQuery, enabled = true) {
	return useQuery({
		queryKey: keywordKeys.list(query),
		queryFn: ({ signal }) => apiRequest(ListKeywords, { query }, { signal }),
		enabled,
	});
}

export function useAddKeywordMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: AddKeywordDto) => apiRequest(AddKeyword, { body }),
		onSuccess: (keywordOrError) => {
			if ('error' in keywordOrError) return;

			const keyword = keywordOrError;
			queryClient.setQueryData(keywordKeys.view(keyword.id), keyword);
			void queryClient.invalidateQueries({
				queryKey: keywordKeys.allList(),
			});
		},
	});
}

export function useEditKeywordMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			keywordId,
			edits,
		}: {
			keywordId: number;
			edits: EditKeywordDto;
		}) =>
			apiRequest(EditKeyword, {
				params: { keywordId },
				body: edits,
			}),
		onSuccess: (keywordOrError) => {
			if ('error' in keywordOrError) return;

			const keyword = keywordOrError;
			queryClient.setQueryData(keywordKeys.view(keyword.id), keyword);
			void queryClient.invalidateQueries({
				queryKey: keywordKeys.allList(),
			});
		},
	});
}

export function useDeleteKeywordMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (keywordId: number) =>
			apiRequest(DeleteKeyword, { params: { keywordId } }),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: keywordKeys.allList(),
			});
		},
	});
}
